import { useState, useCallback, createContext, useContext } from 'react';

export interface ToastOptions {
  type: 'success' | 'error';
  message: string;
  autoClose?: boolean;
  autoCloseDelay?: number;
}

export interface ToastContextType {
  isVisible: boolean;
  config: ToastOptions | null;
  showToast: (options: ToastOptions) => void;
  hideToast: () => void;
  showSuccess: (message: string, autoClose?: boolean, autoCloseDelay?: number) => void;
  showError: (message: string, autoClose?: boolean, autoCloseDelay?: number) => void;
  showPinMismatch: () => void;
  showPinSuccess: () => void;
  showEmailSent: () => void;
  showAccountDeleted: () => void;
  showSubscriptionCanceled: () => void;
  showNetworkError: () => void;
  showGenericError: () => void;
}

export const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Hook for creating toast functionality (used by ToastProvider)
export const useToastState = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [config, setConfig] = useState<ToastOptions | null>(null);

  const showToast = useCallback((options: ToastOptions) => {
    setConfig({
      autoClose: true,
      autoCloseDelay: 4000,
      ...options
    });
    setIsVisible(true);
  }, []);

  const hideToast = useCallback(() => {
    setIsVisible(false);
    // Clear config after animation completes
    setTimeout(() => {
      setConfig(null);
    }, 300);
  }, []);

  // Predefined toast types
  const showSuccess = useCallback((message: string, autoClose = true, autoCloseDelay = 4000) => {
    showToast({
      type: 'success',
      message,
      autoClose,
      autoCloseDelay
    });
  }, [showToast]);

  const showError = useCallback((message: string, autoClose = true, autoCloseDelay = 4000) => {
    showToast({
      type: 'error',
      message,
      autoClose,
      autoCloseDelay
    });
  }, [showToast]);

  // Predefined messages for common scenarios
  const showPinMismatch = useCallback(() => {
    showError("The PINs don't match. Try again.");
  }, [showError]);

  const showPinSuccess = useCallback(() => {
    showSuccess("Your PIN is ready.");
  }, [showSuccess]);

  const showEmailSent = useCallback(() => {
    showSuccess("Verification email has been sent.");
  }, [showSuccess]);

  const showAccountDeleted = useCallback(() => {
    showSuccess("Your account has been deleted successfully.");
  }, [showSuccess]);

  const showSubscriptionCanceled = useCallback(() => {
    showSuccess("Your subscription has been canceled.");
  }, [showSuccess]);

  const showNetworkError = useCallback(() => {
    showError("Network error. Please check your connection and try again.");
  }, [showError]);

  const showGenericError = useCallback(() => {
    showError("Something went wrong. Please try again.");
  }, [showError]);

  return {
    isVisible,
    config,
    showToast,
    hideToast,
    showSuccess,
    showError,
    showPinMismatch,
    showPinSuccess,
    showEmailSent,
    showAccountDeleted,
    showSubscriptionCanceled,
    showNetworkError,
    showGenericError
  };
}; 
